import {
  GlobeAltIcon,
  EnvelopeIcon,
  CpuChipIcon,
  ComputerDesktopIcon,
  MapPinIcon,
  CalendarIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ClockIcon,
  ArrowLeftIcon,
  UserIcon,
  FingerPrintIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

// Entity type configuration
const entityConfig = {
  ip: {
    icon: GlobeAltIcon,
    color: 'bg-blue-100 text-blue-600',
    label: 'IP Address',
  },
  email: {
    icon: EnvelopeIcon,
    color: 'bg-green-100 text-green-600',
    label: 'Email',
  },
  user: {
    icon: UserIcon,
    color: 'bg-purple-100 text-purple-600',
    label: 'User ID',
  },
  device: {
    icon: CpuChipIcon,
    color: 'bg-orange-100 text-orange-600',
    label: 'Device',
  },
  session: {
    icon: FingerPrintIcon,
    color: 'bg-cyan-100 text-cyan-600',
    label: 'Session',
  },
  browser: {
    icon: ComputerDesktopIcon,
    color: 'bg-indigo-100 text-indigo-600',
    label: 'Browser',
  },
  country: {
    icon: FlagIcon,
    color: 'bg-yellow-100 text-yellow-600',
    label: 'Country',
  },
};

// Country flag emoji
function getCountryFlag(countryCode) {
  if (!countryCode || countryCode.length !== 2) return null;
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

// Time range labels
const TIME_RANGE_LABELS = {
  '1h': 'Last hour',
  '6h': 'Last 6 hours',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

// Stat card component
function StatCard({ icon: Icon, label, value, sublabel, color = 'bg-gray-100 text-gray-600' }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
        {sublabel && <p className="text-xs text-gray-400">{sublabel}</p>}
      </div>
    </div>
  );
}

export default function ActivityHeader({
  entity,
  summary,
  stats,
  geo,
  security,
  timeRange,
  onTimeRangeChange,
  loading = false,
}) {
  const navigate = useNavigate();
  const config = entityConfig[entity?.type] || entityConfig.ip;
  const EntityIcon = config.icon;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-gray-200 rounded-xl" />
          <div className="flex-1">
            <div className="h-6 bg-gray-200 rounded w-48 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-32" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header Bar */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            title="Go back"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">Activity Profile</h2>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center gap-2">
          <ClockIcon className="w-4 h-4 text-gray-400" />
          <select
            value={timeRange}
            onChange={(e) => onTimeRangeChange(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {Object.entries(TIME_RANGE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Entity Info */}
      <div className="p-6">
        <div className="flex items-start gap-4 mb-6">
          {/* Entity Icon */}
          <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${config.color}`}>
            <EntityIcon className="w-8 h-8" />
          </div>

          {/* Entity Details */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-gray-500 uppercase">{config.label}</span>
              {security?.threatLevel && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  security.threatLevel === 'critical' ? 'bg-red-100 text-red-700' :
                  security.threatLevel === 'high' ? 'bg-orange-100 text-orange-700' :
                  security.threatLevel === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {security.threatLevel} risk
                </span>
              )}
            </div>
            <h1 className="text-xl font-bold text-gray-900 font-mono break-all">
              {entity?.displayName || entity?.id}
            </h1>

            {/* Geo info for IP entities */}
            {geo && (
              <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                <MapPinIcon className="w-4 h-4" />
                <span>
                  {getCountryFlag(geo.countryCode)} {geo.city ? `${geo.city}, ` : ''}{geo.country}
                </span>
                {geo.isp && <span className="text-gray-400">({geo.isp})</span>}
              </div>
            )}

            {/* First/Last seen */}
            {summary && (
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  First seen: {new Date(summary.firstSeen).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1">
                  <ClockIcon className="w-3.5 h-3.5" />
                  Last seen: {new Date(summary.lastSeen).toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Security Score */}
          {security && (
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center border-4 ${
                security.riskScore >= 80 ? 'border-red-500 bg-red-50' :
                security.riskScore >= 60 ? 'border-orange-500 bg-orange-50' :
                security.riskScore >= 40 ? 'border-yellow-500 bg-yellow-50' :
                'border-green-500 bg-green-50'
              }`}>
                <span className={`text-xl font-bold ${
                  security.riskScore >= 80 ? 'text-red-600' :
                  security.riskScore >= 60 ? 'text-orange-600' :
                  security.riskScore >= 40 ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {security.riskScore}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Risk Score</p>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={ChartBarIcon}
            label="Total Events"
            value={summary?.totalEvents?.toLocaleString() || 0}
            sublabel={`${summary?.uniqueDays || 0} active days`}
            color="bg-blue-100 text-blue-600"
          />
          <StatCard
            icon={UserIcon}
            label="Logins"
            value={stats?.logins || 0}
            sublabel={stats?.failedLogins > 0 ? `${stats.failedLogins} failed` : null}
            color="bg-green-100 text-green-600"
          />
          <StatCard
            icon={GlobeAltIcon}
            label="API Requests"
            value={stats?.apiRequests || 0}
            sublabel={stats?.httpRequests > 0 ? `+${stats.httpRequests} HTTP` : null}
            color="bg-purple-100 text-purple-600"
          />
          <StatCard
            icon={ShieldCheckIcon}
            label="Errors"
            value={stats?.errors || 0}
            sublabel={stats?.wsConnections > 0 ? `${stats.wsConnections} WS sessions` : null}
            color={stats?.errors > 0 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}
          />
        </div>

        {/* Security Flags */}
        {security?.flags?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">Security Flags</p>
            <div className="flex flex-wrap gap-2">
              {security.flags.map((flag, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                >
                  {flag.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
